/**
 * assets/cart-drawer.js
 * Gedrag van sections/cart-drawer.liquid
 *
 * Geknipt uit de inline <script> van dat bestand op 2026-08-15, 1:1.
 * Data komt uit dat bestand, gedrag staat hier. Zie ARCHITECTURE.md sectie 11.
 */

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-cart-overlay]');
      this.panel = this.querySelector('[data-cart-panel]');
    }

    connectedCallback() {
      this.bindEvents();
      document.addEventListener('cart:refresh', () => this.refresh());
      document.querySelectorAll('[data-cart-trigger]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });
      window.CartDrawer = { open: () => this.open(), close: () => this.close(), refresh: () => this.refresh() };
    }

    bindEvents() {
      this.querySelectorAll('[data-cart-close]').forEach(btn =>
        btn.addEventListener('click', () => this.close())
      );
      const overlay = this.querySelector('[data-cart-overlay]');
      if (overlay) overlay.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-qty-change]').forEach(btn => {
        btn.addEventListener('click', () => {
          const line = btn.dataset.line;
          const qty = parseInt(btn.dataset.qty);
          this.updateQuantity(line, qty);
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
    }

    open() {
      /* Alleen een echte open telt als 'cart:open'. refresh() roept open()
         opnieuw aan als de drawer al openstond (zie regel met wasOpen), en een
         hoeveelheidswijziging is geen nieuwe winkelwagenweergave. Zonder deze
         guard vuurde view_cart dubbel; gemeten 16-08-2026 in headless Chrome. */
      var wasDicht = this.getAttribute('aria-hidden') !== 'false';

      this.classList.remove('pointer-events-none');
      this.setAttribute('aria-hidden', 'false');

      /* Signaal voor meetcode en andere componenten. assets/ga4-ecommerce.js
         vuurt hierop view_cart met verse data uit /cart.js. */
      if (wasDicht) document.dispatchEvent(new CustomEvent('cart:open'));
      const overlay = this.querySelector('[data-cart-overlay]');
      const panel = this.querySelector('[data-cart-panel]');
      if (overlay) {
        overlay.classList.remove('opacity-0', 'pointer-events-none');
        overlay.classList.add('opacity-100', 'pointer-events-auto');
      }
      if (panel) {
        panel.classList.remove('translate-x-full');
        panel.classList.add('translate-x-0');
      }
      document.body.style.overflow = 'hidden';
      if (window.FocusTrap && panel) {
        this._focusTrap = new FocusTrap(panel);
        this._focusTrap.activate();
      }
    }

    close() {
      const panel = this.querySelector('[data-cart-panel]');
      const overlay = this.querySelector('[data-cart-overlay]');
      if (panel) {
        panel.classList.remove('translate-x-0');
        panel.classList.add('translate-x-full');
      }
      if (overlay) {
        overlay.classList.remove('opacity-100', 'pointer-events-auto');
        overlay.classList.add('opacity-0', 'pointer-events-none');
      }
      if (this._focusTrap) this._focusTrap.deactivate();
      setTimeout(() => {
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }, 300);
    }

    async refresh() {
      try {
        const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const html = json['cart-drawer'];
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newDrawer = tmp.querySelector('cart-drawer') || tmp.querySelector('#cart-drawer');
        if (newDrawer) {
          const wasOpen = this.getAttribute('aria-hidden') === 'false';
          this.innerHTML = newDrawer.innerHTML;
          this.overlay = this.querySelector('[data-cart-overlay]');
          this.panel = this.querySelector('[data-cart-panel]');
          this.bindEvents();
          if (wasOpen) this.open();
        }
        const cartData = await fetch('/cart.js').then(r => r.json());
        document.querySelectorAll('[data-header-cart-count]').forEach(el => {
          el.textContent = cartData.item_count;
          el.classList.toggle('hidden', cartData.item_count === 0);
        });
        const liveRegion = document.getElementById('AjaxLiveRegion');
        if (liveRegion) liveRegion.textContent = `Winkelwagen bijgewerkt (${cartData.item_count} items)`;
      } catch(err) {
        console.error('Cart drawer refresh error:', err);
      }
    }

    async updateQuantity(lineKey, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: quantity })
        });
        await this.refresh();
      } catch(err) {
        console.error('Cart update error:', err);
      }
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  /* ─── Cross-sell: Product Recommendations Loader ───*/
  function loadCrossSellProducts() {
    const container = document.querySelector('[data-crosssell-products]');
    const skeleton = document.querySelector('[data-crosssell-skeleton]');
    if (!container) return;
    const firstItem = document.querySelector('[data-cart-item]');
    if (!firstItem) return;
    const itemLink = firstItem.querySelector('a[href*="/products/"]');
    if (!itemLink) return;
    const productUrl = itemLink.getAttribute('href');
    const handle = productUrl.split('/products/')[1]?.split('?')[0];
    if (!handle) return;

    /* liq-kleine-order-strip-20260924 — besluit Kees 42, 24-09-2026.
       Onder 25 euro subtotaal kost een order geld: gemeten 6,15 brutowinst tegen 9,66 vaste
       pickkost, dus -3,17 per order over 104 orders in veertien dagen. In dat geval vullen we
       deze strook met drie vaste aanvulartikelen in plaats van de ML-aanbevelingen. Geen
       korting en geen extra kosten voor de klant, alleen een aanbod. Boven 25 euro: ongewijzigd. */
    const LIQ_KLEIN_GRENS = 0; /* KILL 2026-10-01 liquiditeitsjager: kill-toets besluit 24-09 gehaald op 256 orders. Aandeel orders onder 25 euro 20,3 -> 20,7 pct (NIET gezakt), stripartikel in 0,00 pct van de orders onder 40 euro. Terugzetten op 2500 zet de strip weer aan. */
    const LIQ_KLEIN_HANDLES = [
      'vijversticks-3-kleurenmix-15-liter-1650-gram',
      'digitale-vijverthermometer-zwembadthermometer-meetsonde-5\u00BAc-70\u00BAc',
      'filterborstel-15-x-30cm'
    ];

    function liqKleineOrderBron() {
      return fetch('/cart.js')
        .then(r => r.json())
        .then(c => {
          if (!c || typeof c.total_price !== 'number' || c.total_price >= LIQ_KLEIN_GRENS) return null;
          return Promise.all(LIQ_KLEIN_HANDLES.map(h =>
            fetch('/products/' + encodeURIComponent(h) + '.js')
              .then(r => (r.ok ? r.json() : null))
              .catch(() => null)
          )).then(ps => {
            const ok = ps.filter(p => p && p.available !== false && p.variants && p.variants[0]);
            return ok.length ? { products: ok } : null;
          });
        })
        .catch(() => null);
    }

    function liqAanbevelingen() {
      return fetch(`/products/${handle}.json`)
        .then(r => r.json())
        .then(data => {
          const productId = data.product.id;
          return fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`);
        })
        .then(r => r.json())
        .then(data => {
          if (!data.products || data.products.length === 0) {
            const itemLink2 = document.querySelector('[data-cart-item] a[href*="/products/"]');
            const handle2 = itemLink2?.getAttribute('href')?.split('/products/')[1]?.split('?')[0];
            if (handle2) {
              return fetch(`/products/${handle2}.json`)
                .then(r => r.json())
                .then(d => fetch(`/recommendations/products.json?product_id=${d.product.id}&limit=4&intent=related`))
                .then(r => r.json());
            }
            return { products: [] };
          }
          return data;
        });
    }

    liqKleineOrderBron()
      .then(klein => {
        if (klein) {
          const kop = document.querySelector('[data-cart-crosssell] h3');
          if (kop) kop.textContent = 'Maak je bestelling compleet';
          return klein;
        }
        return liqAanbevelingen();
      })
      .then(data => {
        if (skeleton) skeleton.remove();
        if (!data.products || data.products.length === 0) {
          const section = document.querySelector('[data-cart-crosssell]');
          if (section) section.style.display = 'none';
          return;
        }
        const cartItemLinks = document.querySelectorAll('[data-cart-item] a[href*="/products/"]');
        const cartHandles = new Set();
        cartItemLinks.forEach(l => {
          const h = l.getAttribute('href')?.split('/products/')[1]?.split('?')[0];
          if (h) cartHandles.add(h);
        });
        const filtered = data.products.filter(p => !cartHandles.has(p.handle));
        if (filtered.length === 0) {
          const section = document.querySelector('[data-cart-crosssell]');
          if (section) section.style.display = 'none';
          return;
        }
        container.innerHTML = filtered.filter(p => p.variants && p.variants[0]).slice(0, 4).map(p => {
          /* kaizen-crosssell-fix-20260706: Ajax recommendations API geeft images als strings en price in centen; oude code las .src op een string waardoor de hele cross-sell brak (TypeError, nooit gerenderd) */
          const rawImg = p.featured_image || (p.images && p.images[0]) || '';
          const imgSrc = typeof rawImg === 'string' ? rawImg : (rawImg && rawImg.src) || '';
          const img = imgSrc ? imgSrc.replace(/\.(jpg|jpeg|png|webp)/, '_120x.$1') : '';
          const rawPrice = p.variants[0].price;
          const cents = typeof rawPrice === 'string' ? Math.round(parseFloat(rawPrice) * 100) : rawPrice;
          const price = (cents / 100).toFixed(2).replace('.', ',');
          const variantId = p.variants[0].id;
          return `
            <div class="shrink-0 w-[120px] group">
              <a href="/products/${p.handle}" class="block w-full aspect-square rounded-lg overflow-hidden bg-slate-100 mb-2 relative">
                ${img ? `<img src="${img}" alt="${p.title}" class="w-full h-full object-contain mix-blend-darken" loading="lazy">` : ''}
              </a>
              <p class="text-xs font-medium text-navy m-0 mb-1 line-clamp-2 leading-snug">${p.title}</p>
              <div class="flex items-center justify-between">
                <span class="text-xs font-bold text-navy">€${price}</span>
                <button type="button" class="w-7 h-7 flex items-center justify-center rounded-full bg-aqua/10 text-aqua-donker hover:bg-koi hover:text-white transition-all border-none cursor-pointer" data-crosssell-add="${variantId}" aria-label="${p.title} toevoegen">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
            </div>
          `;
        }).join('');
        container.querySelectorAll('[data-crosssell-add]').forEach(btn => {
          btn.addEventListener('click', async () => {
            const vid = btn.dataset.crosssellAdd;
            btn.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93"/></svg>';
            try {
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: parseInt(vid), quantity: 1 }] })
              });
              document.dispatchEvent(new Event('cart:refresh'));
            } catch(err) {
              console.error('Cross-sell add error:', err);
            }
          });
        });
      })
      .catch(err => {
        console.error('Cross-sell load error:', err);
        if (skeleton) skeleton.remove();
      });
  }

  document.addEventListener('DOMContentLoaded', loadCrossSellProducts);
  document.addEventListener('cart:refresh', () => {
    setTimeout(loadCrossSellProducts, 500);
  });
